import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Calendar, Tag, ChevronRight, Phone, MessageCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogList = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'auto', 'meubles'

  useEffect(() => {
    fetchPosts();
  }, [filter]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const url = filter === 'all' 
        ? `${API_URL}/api/blog`
        : `${API_URL}/api/blog?category=${filter}`;
      
      const response = await fetch(url);
      const data = await response.json();
      setPosts(data);
    } catch (error) {
      console.error('Erreur lors du chargement des articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const phoneNumber = "0665976864";

  const handleCallClick = () => {
    window.location.href = `tel:${phoneNumber}`;
  };

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent("Bonjour, je souhaite obtenir plus d'informations sur vos services.");
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
  };

  return (
    <>
      <Helmet>
        <title>Blog Nettoyage Auto & Meubles - BeauClean Bordeaux</title>
        <meta name="description" content="Conseils et astuces pour le nettoyage intérieur de voiture, canapés et meubles à Bordeaux. Articles d'experts par BeauClean." />
        <link rel="canonical" href="https://beauclean33.fr/blog" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        {/* Header avec CTA */}
        <header className="bg-gradient-to-r from-blue-600 to-blue-700 text-white py-6 shadow-lg sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div>
                <Link to="/" className="text-2xl sm:text-3xl font-bold hover:opacity-90 transition-opacity">
                  BeauClean
                </Link>
                <p className="text-blue-100 text-sm mt-1">Blog Conseils & Astuces</p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleWhatsAppClick}
                  className="flex items-center gap-2 bg-green-500 hover:bg-green-600 px-4 py-2 rounded-lg font-semibold transition-all duration-200 shadow-md hover:shadow-lg text-sm sm:text-base"
                >
                  <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                  WhatsApp
                </button>
                <button
                  onClick={handleCallClick}
                  className="flex items-center gap-2 bg-white text-blue-600 hover:bg-blue-50 px-4 py-2 rounded-lg font-semibold transition-all duration-200 shadow-md hover:shadow-lg text-sm sm:text-base"
                >
                  <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
                  Appeler
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white py-12 sm:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Blog BeauClean
            </h1>
            <p className="text-lg sm:text-xl text-blue-100 max-w-3xl mx-auto">
              Conseils d'experts pour l'entretien de votre voiture et de vos meubles à Bordeaux
            </p>
          </div>
        </div>

        {/* Filtres */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
          <div className="bg-white rounded-xl shadow-lg p-4 flex flex-wrap gap-3 justify-center">
            <button
              onClick={() => setFilter('all')}
              className={`px-6 py-2 rounded-lg font-medium transition-all duration-200 ${
                filter === 'all'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Tous les articles
            </button>
            <button
              onClick={() => setFilter('auto')}
              className={`px-6 py-2 rounded-lg font-medium transition-all duration-200 ${
                filter === 'auto'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              🚗 Auto
            </button>
            <button
              onClick={() => setFilter('meubles')}
              className={`px-6 py-2 rounded-lg font-medium transition-all duration-200 ${
                filter === 'meubles'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              🛋️ Meubles
            </button>
          </div>
        </div>

        {/* Liste des articles */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">Chargement des articles...</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-600 text-lg">Aucun article trouvé.</p>
            </div>
          ) : (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {posts.map((post) => (
                <article
                  key={post.id}
                  className="bg-white rounded-xl shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden group flex flex-col"
                >
                  {/* Image d'illustration */}
                  {post.image_url && (
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={post.image_url} 
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute top-3 left-3">
                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold backdrop-blur-sm ${
                          post.category === 'auto'
                            ? 'bg-blue-600/90 text-white'
                            : 'bg-purple-600/90 text-white'
                        }`}>
                          {post.category === 'auto' ? '🚗 Auto' : '🛋️ Meubles'}
                        </span>
                      </div>
                    </div>
                  )}
                  
                  <div className="p-6 flex flex-col flex-grow">
                    {/* Catégorie badge si pas d'image */}
                    {!post.image_url && (
                      <div className="mb-3">
                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
                          post.category === 'auto'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-purple-100 text-purple-700'
                        }`}>
                          {post.category === 'auto' ? '🚗 Auto' : '🛋️ Meubles'}
                        </span>
                      </div>
                    )}

                    {/* Titre */}
                    <h2 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors line-clamp-2">
                      {post.title}
                    </h2>

                    {/* Excerpt */}
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>

                    {/* Métadonnées */}
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(post.created_at)}</span>
                      </div>
                    </div>

                    {/* Keywords */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {post.keywords.slice(0, 3).map((keyword, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs"
                        >
                          <Tag className="w-3 h-3" />
                          {keyword}
                        </span>
                      ))}
                    </div>

                    {/* Lien */}
                    <Link
                      to={`/blog/${post.slug}`}
                      className="inline-flex items-center gap-2 text-blue-600 font-semibold hover:gap-3 transition-all duration-200"
                    >
                      Lire l'article
                      <ChevronRight className="w-5 h-5" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>

        {/* Footer CTA */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white py-12 sm:py-16 mt-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
              Besoin d'un nettoyage professionnel ?
            </h2>
            <p className="text-lg text-blue-100 mb-8">
              Contactez BeauClean pour un service de qualité à Bordeaux et dans un rayon de 40km
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleWhatsAppClick}
                className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 px-8 py-4 rounded-lg font-bold text-lg transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <MessageCircle className="w-6 h-6" />
                WhatsApp
              </button>
              <button
                onClick={handleCallClick}
                className="flex items-center justify-center gap-2 bg-white text-blue-600 hover:bg-blue-50 px-8 py-4 rounded-lg font-bold text-lg transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <Phone className="w-6 h-6" />
                06 65 97 68 64
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-gray-900 text-gray-300 py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-sm">
              © 2024 BeauClean - Nettoyage automobile et meubles à Bordeaux
            </p>
            <div className="flex justify-center gap-6 mt-4">
              <Link to="/" className="hover:text-white transition-colors">
                Accueil
              </Link>
              <Link to="/meubles" className="hover:text-white transition-colors">
                Meubles
              </Link>
              <Link to="/blog" className="hover:text-white transition-colors">
                Blog
              </Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default BlogList;
